import type { Guide } from './types';

export const guide: Guide = {
  id: 'france-buying-costs',
  category: 'provence',
  relatedGuideIds: ['buying-property-provence', 'provence-lifestyle-guide', 'mas-provence-buying'],
  relatedAreaSlugs: ['aix-en-provence', 'gordes', 'luberon'],
  locales: {
    en: {
      slug: 'france-buying-costs',
      title: 'Notaire Fees & Buying Costs When Purchasing Property in France',
      description:
        'A complete breakdown of notaire fees, stamp duty, agency costs, and annual ownership charges when buying property in France — with worked examples at €500k, €1M, and €2M.',
      keywords: [
        'notaire fees france',
        'buying costs france property',
        'frais de notaire france',
        'stamp duty france property',
        'how much are notaire fees in france',
      ],
      heroImage: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1920&q=80',
      readingTime: 12,
      lastUpdated: '2026-06-24',
      sections: [
        {
          id: 'overview',
          heading: 'Understanding the True Cost of Buying in France',
          content:
            'When budgeting for a property purchase in Provence or anywhere in France, the advertised price is only part of the equation. Acquisition costs — dominated by notaire fees and registration taxes — can add **7–8% to the purchase price** for existing properties, or **2–3%** for new-build (VEFA) purchases.\n\nThese costs are not negotiable in the way agency fees sometimes are. They are set by statute, collected by the notaire, and paid at completion. International buyers who fail to budget for them often find themselves short of funds at the acte authentique — a situation Stone Investment helps clients avoid through transparent cost modelling from the first viewing.\n\nThis guide provides a complete breakdown of every cost line item, compares old versus new property acquisition, and offers worked examples at three price points relevant to the Provence market.',
        },
        {
          id: 'notaire-fees-explained',
          heading: 'What Are Notaire Fees in France?',
          content:
            'The term "frais de notaire" is somewhat misleading. Notaire fees comprise three distinct components: the notaire\'s own emoluments (roughly 1% of the purchase price), the **registration tax** (droits de mutation — the largest element), and disbursements (débours) for land registry searches and formalities.\n\nFor **existing properties** (ancien), total notaire fees typically amount to **7–8%** of the purchase price. In departments such as Vaucluse and Var, the registration tax alone is approximately 5.8% of the price, plus departmental and communal surcharges.\n\nFor **new-build properties** (neuf/VEFA), a reduced rate applies — total fees of approximately **2–3%**, as the transaction is subject to VAT (20%) included in the purchase price rather than transfer duty.\n\nThe notaire collects all sums at completion and distributes them to the state, local authorities, and relevant registries. You receive a detailed décompte (breakdown) with your final deed.',
          keyFacts: [
            { label: 'Existing property (ancien)', value: '7–8% of purchase price' },
            { label: 'New-build (VEFA/neuf)', value: '2–3% of purchase price' },
            { label: 'Notaire emoluments', value: '~1% of purchase price' },
            { label: 'Registration tax (ancien)', value: '~5.8% (varies by department)' },
          ],
        },
        {
          id: 'registration-tax',
          heading: 'Registration Tax (Droits de Mutation)',
          content:
            'Droits de mutation — often called stamp duty in English — is the principal tax on property transfers in France. It is calculated on the purchase price (or the valeur vénale if higher) and varies slightly by department.\n\nIn most of Provence (Vaucluse, Bouches-du-Rhône, Var, Alpes-de-Haute-Provence), the rate is approximately **5.80%**, comprising 3.80% departmental tax, 1.20% communal tax, and 0.80% state levy. Some departments apply reduced rates for specific buyer categories, but standard international purchases attract the full rate.\n\nNew-build properties are exempt from droits de mutation because VAT at 20% is already included in the purchase price. This is the primary reason new-build acquisition costs are dramatically lower — though the base price per square metre is often higher.\n\nReduced rates of 0.715% apply in rare cases (e.g., certain social housing or specific corporate restructurings), but these do not apply to standard residential purchases by international buyers.',
          callout: {
            type: 'info',
            text: 'Registration tax is calculated on the full purchase price including any furniture value declared separately. Your notaire will advise on the optimal allocation between immovable and movable elements.',
          },
        },
        {
          id: 'agency-fees',
          heading: 'Agency Fees (Honoraires d\'Agence)',
          content:
            'Agency fees in France are typically **3–5% of the purchase price plus VAT (20%)**, though this varies by agency and region. In Provence\'s luxury segment, fees of 4–5% plus VAT are common for full-service agencies.\n\nCrucially, agency fees may be paid by the **buyer** or the **seller** depending on how the property is advertised. French listings state either "FAI" (frais d\'agence inclus — fees included in the price, paid by seller) or "honoraires charge acquéreur" (fees paid by buyer, added on top of the price).\n\nAlways clarify who pays before making an offer. A property listed at €1,000,000 FAI costs the buyer €1,000,000 plus notaire fees. The same property with buyer-paid agency fees at 5% would cost €1,050,000 plus notaire fees — a meaningful difference.\n\nStone Investment operates with transparent fee structures, disclosed upfront before any viewing commitment.',
          keyFacts: [
            { label: 'Typical agency fee', value: '3–5% + VAT (20%)' },
            { label: 'FAI (fees included)', value: 'Paid by seller' },
            { label: 'Charge acquéreur', value: 'Paid by buyer (added to price)' },
            { label: 'Effective buyer cost (charge acquéreur)', value: '3.6–6% including VAT' },
          ],
        },
        {
          id: 'mortgage-costs',
          heading: 'Mortgage and Financing Costs',
          content:
            'If you finance your Provence purchase with a French mortgage, additional costs apply beyond the property acquisition fees.\n\n**Mortgage arrangement fee (frais de dossier):** €500–€1,500 depending on the bank. Some banks waive this for premium clients.\n\n**Mortgage guarantee:** French banks require a guarantee — either a **hypothèque** (registered charge, costing approximately 1–2% of the loan amount including notaire fees for registration) or an **caution bancaire** (bank guarantee, typically 1–1.5% of the loan, non-refundable premium).\n\n**Life insurance (assurance décès invalidité):** Mandatory for most French mortgages, typically 0.2–0.4% of the outstanding loan balance annually.\n\n**Property valuation (expertise immobilière):** €300–€600, required by most lenders.\n\nNon-resident buyers can obtain French mortgages, typically up to 50–70% loan-to-value, at rates that have remained competitive. Stone Investment connects clients with banks experienced in cross-border lending.',
        },
        {
          id: 'sci-costs',
          heading: 'SCI Setup and Running Costs',
          content:
            'If you acquire property through a Société Civile Immobilière (SCI), additional costs apply at setup and annually.\n\n**Formation costs:** €2,000–€5,000 for notarial deed of incorporation, including registration with the greffe du tribunal de commerce. Complex structures with foreign shareholders or holding companies cost more.\n\n**Annual accounting:** €1,500–€3,000 per year for a standard SCI with one property. Increases with rental activity or multiple assets.\n\n**Annual tax return:** SCI must file a tax return (form 2072) even if no income is generated. Non-filing attracts penalties.\n\n**Share transfer on sale:** If you sell the property by transferring SCI shares rather than the property itself, a different tax regime applies (taxe de publicité foncière at 5% on share transfers above certain thresholds). This is a specialist area requiring tax advice.\n\nFor many international buyers, direct personal ownership is simpler and cheaper. SCI structures justify their cost when estate planning or multi-owner coordination requires them.',
          callout: {
            type: 'warning',
            text: 'SCI ownership adds annual compliance obligations. Failure to file returns or hold annual general meetings can create legal and tax complications on eventual sale.',
          },
        },
        {
          id: 'old-vs-new',
          heading: 'Old Property vs New-Build: Cost Comparison',
          content:
            'The choice between existing and new-build property has significant cost implications beyond the purchase price itself.',
          table: {
            headers: ['Cost Element', 'Existing (Ancien)', 'New-Build (VEFA/Neuf)'],
            rows: [
              ['Notaire fees / transfer costs', '7–8% of price', '2–3% of price'],
              ['Registration tax', '~5.8% (included above)', 'Exempt (VAT applies instead)'],
              ['VAT', 'Not applicable', '20% (included in price)'],
              ['Price per m² (Provence prime)', 'Varies — often lower base', 'Typically 10–20% premium'],
              ['Renovation budget', 'Often required (€1,500–€3,000/m²)', 'Minimal — new condition'],
              ['Total acquisition overhead', '7–8%', '2–3%'],
            ],
          },
          callout: {
            type: 'tip',
            text: 'A new-build at €800,000 with 2.5% fees costs €820,000 all-in. An existing property at €700,000 with 7.5% fees costs €752,500 — despite the lower headline price. Always compare total acquisition cost.',
          },
        },
        {
          id: 'worked-examples',
          heading: 'Worked Examples: €500k, €1M, and €2M',
          content:
            'The following examples assume an existing property purchase in Vaucluse with agency fees included in the price (FAI). Mortgage costs are excluded.',
          table: {
            headers: ['Cost Item', '€500,000', '€1,000,000', '€2,000,000'],
            rows: [
              ['Purchase price (FAI)', '€500,000', '€1,000,000', '€2,000,000'],
              ['Notaire fees (~7.5%)', '€37,500', '€75,000', '€150,000'],
              ['Total acquisition cost', '€537,500', '€1,075,000', '€2,150,000'],
              ['Overhead percentage', '7.5%', '7.5%', '7.5%'],
              ['Legal/diagnostic reports', '€500–€800', '€500–€800', '€500–€800'],
              ['Mortgage guarantee (if 60% LTV)', '€3,000–€6,000', '€6,000–€12,000', '€12,000–€24,000'],
            ],
          },
          keyFacts: [
            { label: '€500k total budget needed', value: '€537,500 (ancien, FAI)' },
            { label: '€1M total budget needed', value: '€1,075,000 (ancien, FAI)' },
            { label: '€2M total budget needed', value: '€2,150,000 (ancien, FAI)' },
            { label: 'New-build equivalent overhead', value: '2–3% instead of 7–8%' },
          ],
        },
        {
          id: 'annual-costs',
          heading: 'Annual Ownership Costs',
          content:
            'Beyond acquisition, property ownership in France carries recurring charges that international buyers — particularly non-residents — must budget for.',
          table: {
            headers: ['Charge', 'Typical Annual Cost', 'Notes'],
            rows: [
              ['Taxe foncière', '€1,500 – €5,000+', 'Property tax — owner pays, varies by commune'],
              ['Taxe d\'habitation (second home)', '€0 – €3,000+', 'Reformed for primary homes; surcharges in tourist communes'],
              ['Assurance multirisque', '€800 – €2,000', 'Home insurance — mandatory with mortgage'],
              ['Pool maintenance', '€1,500 – €3,000/season', 'If applicable'],
              ['Garden/estate care', '€3,000 – €8,000', 'Larger properties with grounds'],
              ['Gestion locative', '15–25% of rental income', 'If let through an agency'],
              ['Comptable (if SCI)', '€1,500 – €3,000', 'Annual accounts and tax return'],
            ],
          },
        },
        {
          id: 'next-steps',
          heading: 'Plan Your Budget with Stone Investment',
          content:
            'Accurate cost modelling is essential before committing to a property purchase in Provence. Stone Investment provides a detailed acquisition cost estimate for every property in our portfolio — including notaire fees, agency costs, and projected annual charges — so you can make informed decisions without surprises at completion.\n\nWhether you are considering a restored mas in the [Luberon](/en/areas/luberon), a bastide near [Gordes](/en/areas/gordes), or a townhouse in [Aix-en-Provence](/en/areas/aix-en-provence), our advisers will walk you through the complete financial picture.\n\nRead our guide to [buying property in Provence](/en/guides/buying-property-provence) for the full purchase process, or browse our [Provence listings](/en/properties?region=provence) to begin your search.',
        },
      ],
      faqs: [
        {
          question: 'How much are notaire fees when buying property in France?',
          answer:
            'Notaire fees and associated taxes (often called frais de notaire) typically total around 7–8% of the purchase price for existing (ancien) property, and roughly 2–3% for some new-build purchases. The exact split between taxes, disbursements and notary remuneration varies by price and location.',
        },
        {
          question: 'What buying costs should I budget beyond the purchase price?',
          answer:
            'Include frais de notaire, possible agency fees if buyer-paid, mortgage arrangement costs, surveys, and initial insurance. For renovations or rural mas properties, also budget works contingencies.',
        },
        {
          question: 'Are agency commissions paid by the buyer or seller in France?',
          answer:
            'Practice varies by mandate. Many sales are marketed with seller-paid commission, but some listings advertise buyer-paid fees. Always confirm who pays and whether the advertised price is FAI (fees included) before offering.',
        },
        {
          question: 'Do new-build properties have lower acquisition costs?',
          answer:
            'Often yes. Off-plan or new homes can attract lower registration taxes than resale stock, which is why total acquisition costs may fall nearer 2–3% in some cases. Confirm the tax treatment for your specific programme.',
        },
        {
          question: 'How are French property purchase taxes calculated?',
          answer:
            'Transfer taxes and related charges form most of the “frais de notaire” package and scale with price and whether the property is new or existing. Your notaire issues a precise provisional account before completion.',
        },
        {
          question: 'Can Stone Investment estimate total costs before I offer?',
          answer:
            'Yes. Stone Investment provides illustrative cost breakdowns for Provence acquisitions so you can model notaire fees, potential financing costs and cash needed at the acte authentique before you commit.',
        },
      ],
      howTo: {
        name: 'How to Budget French Property Buying Costs',
        description:
          'Steps to estimate and prepare for notaire fees and related acquisition costs in France.',
        totalTime: 'PT720H',
        steps: [
          {
            name: 'Confirm property type',
            text: 'Identify whether you are buying existing stock or a new-build, as tax rates differ.',
          },
          {
            name: 'Request a provisional cost estimate',
            text: 'Ask your notaire or adviser for projected frais de notaire on the offer price.',
          },
          {
            name: 'Add financing and professional fees',
            text: 'Include mortgage costs, surveys and any buyer-side agency fees.',
          },
          {
            name: 'Reserve completion funds',
            text: 'Ensure cash is available for taxes and fees due at the acte authentique.',
          },
          {
            name: 'Reconcile the final notarial account',
            text: 'Review the notaire’s final statement at completion for exact amounts.',
          },
        ],
      },
    },
    fr: {
      slug: 'frais-notaire-achat-immobilier-france',
      title: 'Frais de Notaire et Coûts d\'Acquisition Immobilière en France',
      description:
        'Détail complet des frais de notaire, droits de mutation, honoraires d\'agence et charges annuelles lors d\'un achat immobilier en France — avec exemples chiffrés à 500 000 €, 1 M€ et 2 M€.',
      keywords: [
        'frais de notaire france',
        'frais acquisition immobilier france',
        'droits de mutation',
        'combien frais de notaire',
        'calcul frais de notaire',
      ],
      heroImage: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1920&q=80',
      readingTime: 12,
      lastUpdated: '2026-06-24',
      sections: [
        {
          id: 'overview',
          heading: 'Comprendre le Coût Réel d\'un Achat en France',
          content:
            'Lorsque vous budgétisez un achat immobilier en Provence ou ailleurs en France, le prix affiché n\'est qu\'une partie de l\'équation. Les frais d\'acquisition — dominés par les frais de notaire et les droits de mutation — représentent **7 à 8 % du prix** en ancien, ou **2 à 3 %** en neuf (VEFA).\n\nCes coûts sont fixés par la loi, collectés par le notaire et réglés à l\'acte authentique. Les acheteurs internationaux qui ne les anticipent pas se retrouvent parfois en difficulté au moment de la signature. Stone Investment modélise ces coûts dès la première visite pour éviter toute mauvaise surprise.\n\nCe guide détaille chaque poste de dépense, compare ancien et neuf, et propose des exemples chiffrés aux trois niveaux de prix les plus courants sur le marché provençal.',
        },
        {
          id: 'notaire-fees-explained',
          heading: 'Que Recouvrent les Frais de Notaire ?',
          content:
            'L\'expression « frais de notaire » est partiellement trompeuse. Elle regroupe trois composantes : les émoluments du notaire (environ 1 % du prix), les **droits de mutation** (poste principal) et les débours (formalités, conservation des hypothèques).\n\nEn **ancien**, le total s\'élève à **7–8 %** du prix d\'achat. Dans le Vaucluse ou le Var, les droits de mutation représentent à eux seuls environ 5,8 %.\n\nEn **neuf/VEFA**, un taux réduit s\'applique — **2 à 3 %** au total, la TVA à 20 % étant incluse dans le prix plutôt que les droits de mutation.\n\nLe notaire encaisse l\'ensemble à l\'acte et reverse aux administrations. Un décompte détaillé vous est remis avec l\'acte authentique.',
          keyFacts: [
            { label: 'Bien ancien', value: '7–8 % du prix d\'achat' },
            { label: 'Bien neuf (VEFA)', value: '2–3 % du prix d\'achat' },
            { label: 'Émoluments notaire', value: '~1 % du prix' },
            { label: 'Droits de mutation (ancien)', value: '~5,8 % (selon département)' },
          ],
        },
        {
          id: 'registration-tax',
          heading: 'Les Droits de Mutation',
          content:
            'Les droits de mutation constituent l\'impôt principal sur les transferts immobiliers. Ils sont calculés sur le prix d\'achat (ou la valeur vénale si supérieure) et varient légèrement selon le département.\n\nEn Provence (Vaucluse, Bouches-du-Rhône, Var), le taux est d\'environ **5,80 %** : 3,80 % de taxe départementale, 1,20 % communale et 0,80 % d\'émolument d\'État.\n\nLes biens neufs en VEFA en sont exemptés, la TVA à 20 % étant déjà incluse dans le prix. C\'est la raison principale de l\'écart de frais entre ancien et neuf — même si le prix au mètre carré neuf est souvent plus élevé.\n\nLe taux réduit de 0,715 % ne concerne que des cas exceptionnels (logement social, restructurations spécifiques) et ne s\'applique pas aux achats résidentiels standard.',
          callout: {
            type: 'info',
            text: 'Les droits de mutation sont calculés sur le prix total, y compris la part mobilier le cas échéant. Votre notaire vous conseillera sur la répartition optimale entre immobilier et mobilier.',
          },
        },
        {
          id: 'agency-fees',
          heading: 'Honoraires d\'Agence',
          content:
            'Les honoraires d\'agence s\'élèvent généralement à **3–5 % du prix + TVA (20 %)** en Provence, avec des taux de 4–5 % + TVA courants sur le segment prestige.\n\nPoint crucial : les honoraires peuvent être à la charge du **vendeur** ou de l\'**acquéreur**. Les annonces mentionnent « FAI » (frais d\'agence inclus — charge vendeur) ou « honoraires charge acquéreur » (ajoutés au prix).\n\nClarifiez ce point avant toute offre. Un bien à 1 000 000 € FAI coûte 1 000 000 € + frais de notaire. Le même bien avec honoraires acquéreur à 5 % coûterait 1 050 000 € + frais de notaire.\n\nStone Investment applique une transparence totale sur ses honoraires, communiqués avant tout engagement.',
          keyFacts: [
            { label: 'Honoraires typiques', value: '3–5 % + TVA (20 %)' },
            { label: 'FAI (frais inclus)', value: 'Charge vendeur' },
            { label: 'Charge acquéreur', value: 'Ajoutés au prix' },
            { label: 'Coût effectif acquéreur', value: '3,6–6 % TTC' },
          ],
        },
        {
          id: 'mortgage-costs',
          heading: 'Frais de Financement et de Crédit',
          content:
            'Un crédit immobilier français entraîne des frais complémentaires.\n\n**Frais de dossier :** 500 à 1 500 € selon la banque.\n\n**Garantie :** **hypothèque** (environ 1–2 % du montant emprunté, frais de notaire inclus) ou **caution bancaire** (1–1,5 % du prêt, prime non remboursable).\n\n**Assurance emprunteur :** Obligatoire dans la plupart des cas, 0,2–0,4 % du capital restant dû par an.\n\n**Expertise immobilière :** 300–600 €, exigée par la plupart des établissements.\n\nLes non-résidents peuvent obtenir un crédit en France, généralement jusqu\'à 50–70 % du prix. Stone Investment oriente ses clients vers des banques habituées au financement transfrontalier.',
        },
        {
          id: 'sci-costs',
          heading: 'Coûts de Constitution et de Gestion d\'une SCI',
          content:
            'L\'acquisition via une SCI génère des frais à la création et chaque année.\n\n**Constitution :** 2 000 à 5 000 € pour l\'acte notarié et l\'immatriculation au greffe. Les structures complexes avec associés étrangers coûtent davantage.\n\n**Comptabilité annuelle :** 1 500 à 3 000 € pour une SCI standard avec un bien. Plus en cas de location ou de pluralité d\'actifs.\n\n**Déclaration fiscale :** La SCI dépose une liasse fiscale (formulaire 2072) même sans revenus. Le non-dépôt expose à des pénalités.\n\n**Cession de parts :** La vente par transfert de parts sociales obéit à un régime fiscal distinct (taxe de publicité foncière à 5 % au-delà de certains seuils).\n\nPour beaucoup d\'acheteurs internationaux, la détention directe reste plus simple et économique.',
          callout: {
            type: 'warning',
            text: 'La SCI impose des obligations comptables et fiscales annuelles. L\'absence de déclaration ou d\'assemblée générale peut compliquer une future cession.',
          },
        },
        {
          id: 'old-vs-new',
          heading: 'Ancien vs Neuf : Comparatif des Coûts',
          content:
            'Le choix entre un bien ancien et un programme neuf a des implications financières significatives au-delà du prix affiché.',
          table: {
            headers: ['Poste de coût', 'Ancien', 'Neuf (VEFA)'],
            rows: [
              ['Frais de notaire / transfert', '7–8 % du prix', '2–3 % du prix'],
              ['Droits de mutation', '~5,8 % (inclus ci-dessus)', 'Exonérés (TVA à la place)'],
              ['TVA', 'Non applicable', '20 % (incluse dans le prix)'],
              ['Prix au m² (Provence prime)', 'Base souvent plus basse', 'Prime de 10–20 % typique'],
              ['Budget rénovation', 'Souvent nécessaire (1 500–3 000 €/m²)', 'Minimal — état neuf'],
              ['Surcoût total d\'acquisition', '7–8 %', '2–3 %'],
            ],
          },
          callout: {
            type: 'tip',
            text: 'Un neuf à 800 000 € avec 2,5 % de frais coûte 820 000 € tout compris. Un ancien à 700 000 € avec 7,5 % de frais coûte 752 500 €. Comparez toujours le coût total d\'acquisition.',
          },
        },
        {
          id: 'worked-examples',
          heading: 'Exemples Chiffrés : 500 000 €, 1 M€ et 2 M€',
          content:
            'Les exemples suivants concernent un achat en ancien dans le Vaucluse, honoraires d\'agence inclus (FAI). Frais de crédit exclus.',
          table: {
            headers: ['Poste', '500 000 €', '1 000 000 €', '2 000 000 €'],
            rows: [
              ['Prix d\'achat (FAI)', '500 000 €', '1 000 000 €', '2 000 000 €'],
              ['Frais de notaire (~7,5 %)', '37 500 €', '75 000 €', '150 000 €'],
              ['Coût total d\'acquisition', '537 500 €', '1 075 000 €', '2 150 000 €'],
              ['Surcoût en pourcentage', '7,5 %', '7,5 %', '7,5 %'],
              ['Diagnostics / juridique', '500–800 €', '500–800 €', '500–800 €'],
              ['Garantie crédit (si 60 % LTV)', '3 000–6 000 €', '6 000–12 000 €', '12 000–24 000 €'],
            ],
          },
          keyFacts: [
            { label: 'Budget total (500 k€)', value: '537 500 € (ancien, FAI)' },
            { label: 'Budget total (1 M€)', value: '1 075 000 € (ancien, FAI)' },
            { label: 'Budget total (2 M€)', value: '2 150 000 € (ancien, FAI)' },
            { label: 'Surcoût neuf équivalent', value: '2–3 % au lieu de 7–8 %' },
          ],
        },
        {
          id: 'annual-costs',
          heading: 'Charges Annuelles de Propriété',
          content:
            'Au-delà de l\'acquisition, la propriété en France génère des charges récurrentes que les non-résidents doivent anticiper.',
          table: {
            headers: ['Charge', 'Coût annuel type', 'Remarques'],
            rows: [
              ['Taxe foncière', '1 500 – 5 000 €+', 'Impôt foncier — charge propriétaire'],
              ['Taxe d\'habitation (secondaire)', '0 – 3 000 €+', 'Réformée pour résidence principale ; surtaxes touristiques'],
              ['Assurance multirisque', '800 – 2 000 €', 'Obligatoire avec crédit'],
              ['Entretien piscine', '1 500 – 3 000 €/saison', 'Si applicable'],
              ['Entretien parc/jardin', '3 000 – 8 000 €', 'Propriétés avec terrain'],
              ['Gestion locative', '15–25 % des loyers', 'Si mise en location'],
              ['Expert-comptable (SCI)', '1 500 – 3 000 €', 'Comptes et liasse fiscale'],
            ],
          },
        },
        {
          id: 'next-steps',
          heading: 'Planifiez Votre Budget avec Stone Investment',
          content:
            'Une modélisation précise des coûts est indispensable avant tout engagement en Provence. Stone Investment fournit une estimation détaillée pour chaque bien — frais de notaire, honoraires et charges annuelles prévisionnelles.\n\nQue vous envisagiez un mas dans le [Luberon](/fr/areas/luberon), une bastide près de [Gordes](/fr/areas/gordes) ou une maison de maître à [Aix-en-Provence](/fr/areas/aix-en-provence), nos conseillers vous présentent le tableau financier complet.\n\nConsultez notre guide sur [l\'achat immobilier en Provence](/fr/guides/acheter-bien-immobilier-provence) ou parcourez nos [biens en Provence](/fr/properties?region=provence).',
        },
      ],
      faqs: [
        {
          question: 'Combien coûtent les frais de notaire en France ?',
          answer:
            'Les frais de notaire (droits et émoluments) représentent souvent environ 7 à 8 % du prix pour un bien ancien, et environ 2 à 3 % pour certains biens neufs. La répartition entre taxes, débours et rémunération du notaire varie selon le prix et la localisation.',
        },
        {
          question: 'Quels coûts budgéter au-delà du prix d\'achat ?',
          answer:
            'Intégrez les frais de notaire, d\'éventuels honoraires d\'agence à la charge de l\'acquéreur, les frais de prêt, diagnostics/expertises et l\'assurance initiale. Pour un mas à rénover, ajoutez une enveloppe travaux.',
        },
        {
          question: 'Qui paie la commission d\'agence en France ?',
          answer:
            'La pratique dépend du mandat. Beaucoup de ventes sont à commission vendeur, d\'autres affichent des honoraires acquéreur. Vérifiez qui paie et si le prix est FAI avant de faire une offre.',
        },
        {
          question: 'Le neuf a-t-il des frais d\'acquisition plus bas ?',
          answer:
            'Souvent oui. Le neuf ou VEFA peut bénéficier de droits d\'enregistrement plus faibles que l\'ancien, d\'où des frais totaux parfois proches de 2 à 3 %. Confirmez le régime fiscal du programme concerné.',
        },
        {
          question: 'Comment sont calculés les droits d\'achat ?',
          answer:
            'Les droits de mutation constituent l\'essentiel du forfait « frais de notaire » et dépendent du prix et du caractère neuf ou ancien. Le notaire établit un compte prévisionnel avant la signature.',
        },
        {
          question: 'Stone Investment peut-il estimer le budget total ?',
          answer:
            'Oui. Stone Investment fournit des ventilations indicatives pour les acquisitions en Provence afin de modéliser frais de notaire, financement et trésorerie nécessaire à l\'acte authentique.',
        },
      ],
      howTo: {
        name: 'Comment budgéter les frais d\'achat immobilier en France',
        description:
          'Étapes pour estimer et préparer les frais de notaire et coûts d\'acquisition associés en France.',
        totalTime: 'PT720H',
        steps: [
          {
            name: 'Identifier le type de bien',
            text: 'Distinguez ancien et neuf, les taux de droits différant sensiblement.',
          },
          {
            name: 'Demander une estimation prévisionnelle',
            text: 'Sollicitez notaire ou conseiller sur les frais de notaire au prix offert.',
          },
          {
            name: 'Ajouter financement et honoraires',
            text: 'Intégrez frais de prêt, expertises et honoraires acquéreur éventuels.',
          },
          {
            name: 'Provisionner la trésorerie de signature',
            text: 'Assurez la disponibilité des fonds pour taxes et frais à l\'acte.',
          },
          {
            name: 'Contrôler le compte définitif',
            text: 'Vérifiez le décompte notarial final le jour de la signature.',
          },
        ],
      },
    },
  },
};
